import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Siren, FolderPlus, Check } from "lucide-react";

/**
 * Banner mare (admin) — proiect nou adăugat de planificator în categoria 6 Sanierungskonzept.
 * Include meldung: creează folderul proiectului pe server. Se reîncarcă la 60s.
 */
export function NewProjectAlertBanner() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [busy, setBusy] = useState({});
  const isAdmin = !!user && user.role === "admin";

  const load = async () => {
    if (!isAdmin) return;
    try {
      const r = await api.get("/project-alerts");
      setAlerts(r.data || []);
    } catch { /* noop */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [isAdmin]);

  const ack = async (a) => {
    setBusy((b) => ({ ...b, [a.id]: true }));
    try {
      await api.post(`/project-alerts/${a.id}/ack`);
      toast.success(`✓ Confirmat — folder creat pentru „${a.project_name}"`);
      setAlerts((prev) => prev.filter((x) => x.id !== a.id));
    } catch { toast.error("Eroare la confirmare"); }
    finally { setBusy((b) => { const c = { ...b }; delete c[a.id]; return c; }); }
  };

  const fmtTs = (s) => {
    if (!s) return "";
    try {
      const d = new Date(s);
      return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch { return s; }
  };

  if (!isAdmin || alerts.length === 0) return null;

  return (
    <div className="mb-4 space-y-2" data-testid="new-project-alert-banner">
      {alerts.map((a) => (
        <div
          key={a.id}
          className="rounded-xl border-4 border-red-500 bg-gradient-to-r from-red-600 to-orange-500 p-4 shadow-2xl animate-pulse-red-soft"
          data-testid={`project-alert-${a.id}`}
        >
          <div className="flex items-start gap-3 flex-wrap">
            <Siren size={32} className="text-white flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[240px]">
              <div className="text-white font-extrabold text-lg sm:text-xl uppercase tracking-wide">
                ⚠ Atenție: Proiect nou adăugat de {a.created_by}
              </div>
              <div className="text-white/95 text-sm mt-1">
                <strong className="text-base">„{a.project_name}"</strong>
                {a.adresa && <span> · 📍 {a.adresa}</span>}
                <span> · categoria <strong>6 Sanierungskonzept</strong></span>
                <span className="text-white/75"> · {fmtTs(a.created_at)}</span>
              </div>
              <div className="mt-2 inline-flex items-center gap-2 bg-white/20 border border-white/40 rounded-lg px-3 py-1.5 text-white font-bold text-sm">
                <FolderPlus size={16} />
                MELDUNG: Creează folderul proiectului pe server!
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => ack(a)}
                disabled={!!busy[a.id]}
                className="px-4 py-2 rounded-lg bg-white text-red-700 font-extrabold text-sm hover:bg-red-50 inline-flex items-center gap-1.5 disabled:opacity-50 shadow"
                data-testid={`project-alert-ack-${a.id}`}
              >
                <Check size={15} /> Folder creat — Confirmă
              </button>
              <button
                onClick={() => nav("/proiecte")}
                className="px-4 py-1.5 rounded-lg bg-white/20 border border-white/50 text-white font-bold text-xs hover:bg-white/30"
                data-testid={`project-alert-open-${a.id}`}
              >
                Vezi proiectele →
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
